import { diff } from 'deep-object-diff'
import { useEffect, useRef, useState } from 'react'
import Select, { components } from 'react-select'
import AsyncSelect from 'react-select/async'
import { get_nested } from '../../../utils/miscellaneous'
import { trans } from '../../../utils/utilsTrans'
import Tooltip from '../Utils/Tooltip'

const borderDefault = 'border border-greyLight7 hover:border-primaryLight2'
const borderDisabled = 'border border-gray-100'
const borderError = 'border border-error'

const Control = (props) => {
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const moreBadgesRef = useRef(null)
  const [displayMore, setDisplayMore] = useState(0)
  let inputStyle = `${
    props.selectProps.menuIsOpen ? 'shadow-input' : ''
  } ${borderDefault}`

  if (props.selectProps.errorRequired) {
    inputStyle = `${
      props.selectProps.menuIsOpen ? 'shadow-input-error' : ''
    } ${borderError}`
  }

  if (props.isDisabled) {
    inputStyle = borderDisabled
  }

  useEffect(() => {
    if (Array.isArray(props.children[0].props.children[0]) && props.isMulti) {
      let count = 0
      const containerWidth = containerRef.current.clientWidth - 28
      let usedWidth = moreBadgesRef.current.clientWidth
      const arr = Array.prototype.slice.call(contentRef.current.children)

      arr.map((v) => {
        if (v.className.includes('item-multi')) {
          v.style.visibility = 'hidden'
          v.style.position = 'absolute'
          v.style.display = 'flex'
          usedWidth = usedWidth + v.clientWidth + 6

          if (usedWidth > containerWidth) {
            count = count + 1
            v.style.display = 'none'
          } else {
            v.style.visibility = 'visible'
            v.style.position = 'static'
          }
        }
      })

      setDisplayMore(count)
    } else {
      setDisplayMore(0)
    }
  }, [props.children[0]?.props?.children[0]?.length])

  return (
    <div
      ref={containerRef}
      {...props.innerProps}
      data-cy={`select-${props.selectProps.id}`} // Pour les tests de notre testeuse préférée
      className={`min-w-[70px] flex bg-backgroundPrimary justify-between rounded-8 block w-full items-center overflow-hidden transition-color duration-200 px-14 cursor-pointer ${inputStyle} ${
        props.isMulti ? 'py-[7px]' : 'py-10'
      }`}
    >
      <div
        ref={contentRef}
        className='flex items-center flex-grow'
        {...props.children[0].props.innerProps}
      >
        {props.children[0].props.children}
        {props.isMulti ? (
          <div
            ref={moreBadgesRef}
            className={`${
              displayMore ? 'opacity-1' : 'opacity-0'
            } px-8 py-4 font-medium leading-base flex items-center border rounded-full border-greyLight7 mr-4 whitespace-nowrap bg-white my-2`}
          >
            <span className='text-sm leading-sm text-primary'>
              + {displayMore} {displayMore >= 2 ? trans('others') : trans('other')}
            </span>
          </div>
        ) : null}
      </div>
      <i
        className={`icon-chevron-down text-sm px-4 flex items-center justify-center ${
          props.isDisabled ? 'text-gray-100' : 'text-greyLight3'
        }`}
      />
    </div>
  )
}

const MultiValueContainer = (props) => {
  const displayValue = props.selectProps.getOptionLabel(props.data)

  return (
    <div className='item-multi pl-8 pr-4 py-4 leading-base flex items-center border rounded-full border-greyLight7 mr-4 bg-white my-2'>
      <span
        className='text-sm leading-sm text-greyLight1 whitespace-nowrap'
        data-cy={props.data?.value}
      >
        {displayValue}
      </span>
      <div
        {...props.children[1].props.innerProps}
        className='flex justify-center items-center'
      >
        <i
          className='icon icon-close ml-8 text-greyLight4 cursor-pointer hover:text-error transition-color duration-200'
          data-cy='cross'
        />
      </div>
    </div>
  )
}

const Menu = (props) => {
  if (props.isLoading) {
    return (
      <components.Menu className='z-40' {...props}>
        <div className='rounded-8 px-14 py-10 text-primary flex items-center space-x-8'>
          <i className='icon-spinner text-primary animate-spin-pulse ' />
          <p>Chargement</p>
        </div>
      </components.Menu>
    )
  } else if (props.selectProps.hideMenu) {
    return null
  } else if (props.options.length) {
    return (
      <components.Menu className='z-40' {...props}>
        {props.children}
      </components.Menu>
    )
  } else {
    return (
      <components.Menu {...props} className='z-40'>
        <span className='block p-16 text-sm leading-sm text-center text-info flex items-center justify-center'>
          <i className='icon icon-info-circle mr-8' />{' '}
          {props.selectProps.noOptionMessage}
        </span>
      </components.Menu>
    )
  }
}

const MenuList = (props) => {
  const [active, setActive] = useState(false)
  const [activeScroll, setActiveScroll] = useState(false)

  useEffect(() => {
    setActive(true)
    setTimeout(() => {
      setActiveScroll(true)
    }, 500)
  }, [])

  return (
    <div
      {...props.innerProps}
      className={
        'p-0 overflow-auto transform-all duration-500 ' +
        (activeScroll ? '' : 'overflow-hidden ') +
        (active ? 'opacity-1' : 'opacity-0')
      }
      style={{ maxHeight: active ? props.selectProps.maxMenuHeight : 0 }}
    >
      {props.children}
    </div>
  )
}

const Placeholder = (props) => {
  return (
    <div
      {...props.innerProps}
      className='row-start-1 row-end-2 col-start-1 col-end-3 text-greyLight3 text-sm leading-[24px] absolute'
    >
      {props.children}
    </div>
  )
}

const Input = (props) => {
  return <components.Input {...props} className='m-0 p-0 w-full' />
}

const SingleValue = (props) => {
  const [text, setText] = useState(props.children)

  useEffect(() => {
    if (!props.selectProps.menuIsOpen) {
      setText(props.children)
    }

    document.addEventListener('keydown', escFunction, false)

    return () => {
      document.removeEventListener('keydown', escFunction, false)
    }
  }, [props.selectProps.menuIsOpen])

  function escFunction(event) {
    if (
      event.keyCode === 8 &&
      props.selectProps.menuIsOpen &&
      props.selectProps.isSearchable
    ) {
      setText('')
    }
  }

  return (
    <span className='flex items-center row-start-1 row-end-2 col-start-1 col-end-3 text-sm text-grey whitespace-nowrap'>
      {props.selectProps.useFlag ? (
        <div
          className={`w-[22px] h-[15px] flag-select mr-8 rounded-[2px] ${props.data.value}`}
          style={{ backgroundImage: 'url(/public/images/flags.png)' }}
        />
      ) : null}
      {text}
    </span>
  )
}

const DefaultOption = ({
  data,
  innerRef,
  innerProps,
  isSelected,
  selectProps,
  isMulti,
  label,
}) => {
  const isSelectedOption =
    isMulti &&
    selectProps.options.length - 1 === selectProps.value?.length &&
    !data.value
      ? true
      : typeof selectProps.value === 'object' &&
        !Array.isArray(selectProps.value)
      ? selectProps.value?.value === data.value
      : isSelected

  const isSelectAll = isMulti && !data.value
  // Conteneur de l‘option si aucun n‘est passé en props par défaut si aucune n
  const ref = useRef(null)
  useEffect(() => {
    // autoScroll vers l‘option sélectionnée à l'ouverture du dropdown
    setTimeout(() => {
      if (ref?.current?.classList.contains('active'))
        ref.current.parentNode.scrollTop = ref.current.offsetTop
    }, 15)
  }, [])

  return data.custom ? (
    <div
      ref={innerRef}
      onClick={() => selectProps.customFunc()}
      className='flex text-primary group items-center px-14 py-10 cursor-pointer border-b border-grey-400 last:border-0 last:rounded-b-4 first:rounded-t-4'
    >
      <i className='icon icon-plus mr-10' /> {trans('flow.add_data_source')}
    </div>
  ) : (
    <div
      ref={ref}
      data-cy={`option-${data.value}`}
      className={`${
        isSelectedOption ? 'bg-primaryLight3' : 'hover:bg-backgroundPrimary'
      } flex group items-center px-14 py-10 cursor-pointer last:border-0 last:rounded-b-4 first:rounded-t-4 ${isSelectAll ? "border-b-2 border-greyLight7" : 'border-b border-grey-400'}`}
      {...innerProps}
    >
      {selectProps.useFlag ? (
        <div
          className={`w-[22px] h-[15px] flag-select mr-8 rounded-[2px] ${data.value}`}
          style={{ backgroundImage: 'url(/public/images/flags.png)' }}
        />
      ) : null}
      {!selectProps.useFlag ? (
        <div
          className={`${
            isSelectedOption
              ? 'bg-primaryLight3 border-primary group-hover:border-primaryDark1'
              : 'border-greyLight6 bg-white group-hover:border-primary'
          } ${
            isMulti ? 'rounded-[6px]' : 'rounded-full'
          } transition-color duration-200 min-h-[19px] min-w-[19px] border-2 mr-8 flex items-center justify-center`}
        >
          {isMulti ? (
            <i
              className={`${
                isSelectedOption
                  ? 'opacity-1 group-hover:text-primaryDark1'
                  : 'opacity-0'
              } icon-check text-primary text-[11px] leading-xs flex items-center justify-center transition-opacity duration-200`}
            />
          ) : (
            <div
              className={`${
                isSelectedOption
                  ? 'opacity-1 group-hover:bg-primaryDark1'
                  : 'opacity-0'
              } bg-primary w-[7px] h-[7px] rounded-full flex items-center justify-center transition-opacity transition-color duration-200`}
            />
          )}
        </div>
      ) : null}
      <span className={`block text-sm ${isSelectAll ? 'font-bold' : ''}`}>
        {label}
      </span>
    </div>
  )
}

const CustomSelect = (props) => {
  const {
    options,
    label,
    Option,
    inputValue,
    isMulti,
    isSearchable,
    placeholder,
    extraClass,
    defaultValue,
    defaultOptions,
    input,
    useFlag,
    disabled,
    onInputChange,
    maxMenuHeight,
    menuPlacement,
    meta,
    id,
    noOptionMessage,
    customError,
    errorBorders,
    loading,
    hideMenu,
    onBlur,
    searchNoResultMessage,
    formatOptionLabel,
    menuIsOpen,
    helpMessage,
    tooltip,
    customFunc,
    isAsync,
    loadOptions,
    cacheOptions,
    onRemove,
    passedRef,
  } = props
  let { value, onChange, customValueKey, customLabelKey } = props

  if (input) {
    value = input.value
    onChange = input.onChange
  }

  if (!Array.isArray(customValueKey)) {
    customValueKey = [customValueKey]
  }

  if (!Array.isArray(customLabelKey)) {
    customLabelKey = [customLabelKey]
  }

  const errorRequired = (meta?.error && meta?.touched) ?? null

  const handleOnChange = (value) => {
    if (value) {
      if (isMulti) {
        let newValues = []

        if (value.some((v) => !v.value) && value.length < options.length) {
          options.map((option) => {
            if (option.value) {
              newValues.push(
                customValueKey ? get_nested(customValueKey, option) : option
              )
            }
          })
        } else if (
          value.some((v) => !v.value) &&
          value.length === options.length
        ) {
          newValues.push([])
        } else {
          value.map((v) => {
            newValues.push(customValueKey ? get_nested(customValueKey, v) : v)
          })
        }

        if (newValues.length === 0) {
          newValues = undefined
        }

        onChange(newValues)
      } else {
        onChange(customValueKey ? get_nested(customValueKey, value) : value)
      }

      if (props.callBack) {
        props.callBack(value)
      }
    } else {
      onChange(null)
    }
  }

  let selectedValue = ''

  if (typeof value === 'object' && !Array.isArray(value)) {
    selectedValue = defaultOptions?.find(
      (option) => !Object.keys(diff(option, value)).length
    )
  } else if (value && !customValueKey) {
    selectedValue = value
  } else if (options) {
    if (isMulti) {
      selectedValue = options.filter((option) =>
        value.includes(get_nested(customValueKey, option))
      )
    } else {
      selectedValue = options.find(
        (option) =>
          JSON.stringify(get_nested(customValueKey, option)) ===
          JSON.stringify(value)
      )
    }
  }

  let Component = Select
  if (isAsync) Component = AsyncSelect

  return (
    <div className={extraClass}>
      <div
        className={`flex justify-between mb-4 items-center ${
          tooltip ? 'mb-4' : label ? 'mb-6' : 'mb-0'
        }`}
      >
        {label ? (
          <label
            className={`flex text-xs leading-xs text-grey ${
              disabled ? 'text-gray-300' : ''
            }`}
          >
            {label}
          </label>
        ) : null}
        {onRemove && value ? (
          <div
            className='text-xs text-error space-x-4 flex items-center cursor-pointer'
            onClick={() => {
              onChange(null)
            }}
          >
            <i className='icon-trash' />
            <span className='block' data-cy='reinitialiser'>
              {trans('reset')}
            </span>
          </div>
        ) : null}
        {tooltip ? <Tooltip text={tooltip} /> : null}
      </div>
      <Component
        noOptionsMessage={() => (
          <span className='text-greyLight3 text-sm'>
            {searchNoResultMessage}
          </span>
        )}
        key={`my_unique_select_key__${input?.name}`}
        ref={passedRef}
        loadOptions={loadOptions}
        cacheOptions={cacheOptions}
        useFlag={useFlag}
        isMulti={isMulti}
        id={id || input?.name}
        blurInputOnSelect={false}
        openMenuOnFocus={true}
        menuPlacement={menuPlacement}
        maxMenuHeight={maxMenuHeight}
        noOptionMessage={noOptionMessage ?? placeholder}
        isSearchable={isSearchable}
        closeMenuOnSelect={!isMulti}
        menuIsOpen={menuIsOpen}
        getOptionValue={(option) => option?.[customValueKey] ?? null}
        getOptionLabel={(option) => option?.[customLabelKey] ?? null}
        formatOptionLabel={formatOptionLabel}
        backspaceRemovesValue={false}
        defaultOptions={defaultOptions}
        defaultValue={defaultValue}
        placeholder={placeholder}
        onBlur={() => {
          if (onBlur) {
            onBlur()
          }
        }}
        isClearable={true}
        options={options}
        isDisabled={disabled}
        isLoading={loading}
        inputValue={inputValue}
        hideSelectedOptions={false}
        onInputChange={onInputChange}
        menuShouldScrollIntoView={true}
        value={selectedValue}
        hideMenu={hideMenu}
        errorRequired={errorRequired || customError || errorBorders}
        onChange={(value) => handleOnChange(value)}
        customFunc={customFunc}
        components={{
          MultiValueContainer,
          Option: Option || DefaultOption,
          Placeholder,
          Control,
          SingleValue,
          Menu,
          MenuList,
          Input,
        }}
      />
      {helpMessage && !customError ? (
        <span className='mt-4 block text-xs leading-xs text-greyLight3'>
          {helpMessage}
        </span>
      ) : null}
      {errorRequired || customError ? (
        <span className='mt-4 text-xs leading-xs block text-error'>
          {customError || trans('templates.field_filled')}
        </span>
      ) : null}
    </div>
  )
}

CustomSelect.defaultProps = {
  customValueKey: ['value'],
  customLabelKey: 'label',
  menuPlacement: 'bottom',
  isSearchable: false,
  isMulti: false,
  value: null,
  noOptionMessage: trans('no_option'),
  searchNoResultMessage: trans('no_result'),
}

export default CustomSelect
